import { useEffect, useRef } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import { Markdown } from "tiptap-markdown";

import { FrameMention, parseFrameLink } from "./FrameMention";
import { KindrawIcon } from "./icons";
import { RichTextAiMenu } from "./RichTextAiMenu";
import { SlashCommand } from "./SlashCommand";
import { useKindrawI18n } from "./i18n";
import { shouldSeed } from "./seedDecision";

import type { FrameRef } from "./FrameMention";
import type { Editor } from "@tiptap/react";
import type { KindrawYjsProvider } from "./yjsProvider";

// tiptap-markdown injeta `storage.markdown` em runtime, mas não expõe tipos.
type MarkdownStorage = {
  markdown?: {
    getMarkdown?: () => string;
    parser?: { parse?: (md: string) => unknown };
  };
};

const getEditorMarkdown = (editor: Editor): string => {
  const storage = editor.storage as MarkdownStorage;
  return storage.markdown?.getMarkdown?.() ?? "";
};

// Editor WYSIWYG "headless" estilo Outline: sem toolbar fixa. Formatação por
// markdown shortcuts ao digitar, bubble menu na seleção e menu de blocos em
// linha vazia. Lê/escreve Markdown por baixo (preview público, share, persistência
// continuam em md). Colar texto é sempre interpretado como Markdown.

type RichTextEditorProps = {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  editable?: boolean;
  // Quando presente, o editor entra em modo colaborativo (Yjs): o conteúdo é
  // governado pelo Y.Doc do provider, não pela prop `value`. `value` ainda é
  // usado como seed inicial pelo dono do documento (ver HybridMarkdownPane).
  // `user` alimenta o caret nomeado do CollaborationCaret (sem ele o caret mostra
  // "User: <clientID>"). Passamos o objeto COMPLETO porque o CollaborationCaret
  // SOBRESCREVE o awareness.user com este objeto — então ele precisa conter os
  // campos que o facepile também lê (userId/avatarUrl/githubLogin), senão eles
  // somem do awareness ao montar o editor.
  collab?: {
    provider: KindrawYjsProvider;
    fieldName: string;
    user: {
      name: string;
      color: string;
      avatarUrl?: string | null;
      githubLogin?: string | null;
      userId?: string | null;
    };
  };
  // Markdown inicial para popular um Y.Doc VAZIO ao entrar na sessão (só o
  // primeiro participante semeia; os demais recebem o estado já sincronizado).
  seedMarkdown?: string;
  // Só um peer AUTORIZADO a semear um doc colaborativo vazio (o editor
  // autoritativo: dono/editor autenticado com permissão de escrita) deve passar
  // `true`. Convidados (guests) NÃO podem semear — eles só recebem o estado já
  // sincronizado. Gating contra a RACE de seed: quando o `yjs-init` chega com
  // `update: null` (DO reciclado / room novo) o `onSynced` dispara com o doc
  // ainda vazio; se o guest semeasse aqui, plantaria o snapshot REST estável
  // (ex.: só "# Payment Method") e colidiria com o conteúdo bom do dono.
  canSeed?: boolean;
  // Ponte com o canvas (só no hybrid): habilita menções "@" a frames e o clique
  // que dá foco no frame. Ausente = sem menção de frame (doc puro).
  frameMention?: {
    getFrames: () => FrameRef[];
    focusFrame: (id: string) => void;
  };
};

const MenuButton = ({
  active,
  label,
  onClick,
  children,
}: {
  active?: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    aria-label={label}
    aria-pressed={active}
    className={`kindraw-rte__menu-btn${
      active ? " kindraw-rte__menu-btn--active" : ""
    }`}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    title={label}
    type="button"
  >
    {children}
  </button>
);

export const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  editable = true,
  collab,
  seedMarkdown,
  canSeed = false,
  frameMention,
}: RichTextEditorProps) => {
  const { t } = useKindrawI18n();
  const effectivePlaceholder =
    placeholder ?? t("kindraw.hybrid.editorPlaceholder");
  // Distingue mudanças internas (digitação) de externas (troca de documento)
  // para só re-setar o conteúdo no segundo caso.
  const lastEmittedRef = useRef<string>(value);
  const isCollab = Boolean(collab);

  const editor = useEditor({
    editable,
    extensions: [
      // No modo colaborativo o histórico vem do Yjs (Collaboration traz undo
      // próprio), então desligamos o history do StarterKit para evitar conflito.
      StarterKit.configure({
        link: {
          openOnClick: false,
          // Permite os links internos kindraw:// (menções a frame, links de
          // seção) sem serem descartados pelo sanitizador de protocolo.
          protocols: ["kindraw"],
        },
        ...(isCollab ? { undoRedo: false } : {}),
      }),
      Highlight,
      // nested: true permite aninhar tarefas; o tiptap-markdown serializa
      // task items como "- [ ]" / "- [x]", então sobrevivem ao round-trip.
      TaskList,
      TaskItem.configure({ nested: true }),
      SlashCommand,
      ...(frameMention
        ? [FrameMention.configure({ getFrames: frameMention.getFrames })]
        : []),
      Markdown.configure({
        html: false,
        linkify: true,
        breaks: true,
        transformPastedText: true,
        transformCopiedText: false,
      }),
      ...(collab
        ? [
            Collaboration.configure({
              document: collab.provider.doc,
              field: collab.fieldName,
            }),
            CollaborationCaret.configure({
              provider: collab.provider,
              user: collab.user,
              // render customizado: esconde carets sem nome válido (fantasmas /
              // peers de versão antiga que mostrariam "User: <clientID>") e
              // desenha um caret nomeado limpo p/ os válidos.
              render: (u: { name?: string | null; color?: string | null }) => {
                const caret = document.createElement("span");
                const name = (u.name || "").trim();
                if (!name || /^User:\s/i.test(name)) {
                  // peer sem identidade real → caret invisível
                  caret.style.display = "none";
                  return caret;
                }
                caret.classList.add("collaboration-carets__caret");
                const color = u.color || "#888";
                caret.setAttribute("style", `border-color: ${color}`);
                const label = document.createElement("div");
                label.classList.add("collaboration-carets__label");
                label.setAttribute("style", `background-color: ${color}`);
                label.textContent = name;
                caret.appendChild(label);
                return caret;
              },
            }),
          ]
        : []),
    ],
    // No modo colaborativo o conteúdo vem do Y.Doc; setar `content` aqui
    // duplicaria o documento. O seed inicial é feito pelo dono fora daqui.
    content: isCollab ? undefined : value,
    editorProps: {
      attributes: {
        class: "kindraw-rte__content",
        "aria-label": effectivePlaceholder,
      },
      // Clique numa menção de frame (link kindraw://frame/<id>) NÃO navega —
      // dá foco no frame do canvas.
      handleClick: (_view, _pos, event) => {
        const anchor = (event.target as HTMLElement | null)?.closest?.("a");
        const href = anchor?.getAttribute("href");
        const frameId = href ? parseFrameLink(href) : null;
        if (frameId && frameMention) {
          event.preventDefault();
          frameMention.focusFrame(frameId);
          return true;
        }
        return false;
      },
      // Colar texto puro = interpretar como Markdown e inserir já formatado.
      handlePaste: (view, event) => {
        const clipboard = event.clipboardData;
        if (!clipboard) {
          return false;
        }
        // Se há HTML no clipboard (cópia rica), deixa o fluxo padrão tratar.
        if (clipboard.types.includes("text/html")) {
          return false;
        }
        const text = clipboard.getData("text/plain");
        if (!text) {
          return false;
        }
        event.preventDefault();
        // insertContent com string passa pelo parser de markdown do tiptap-markdown
        editorRef.current?.commands.insertContent(text);
        return true;
      },
    },
    onUpdate: ({ editor: current }) => {
      const markdown = getEditorMarkdown(current);
      lastEmittedRef.current = markdown;
      onChange(markdown);
    },
  });

  // Ref estável para usar o editor dentro de handlers de editorProps.
  const editorRef = useRef<Editor | null>(null);
  editorRef.current = editor;

  // Parse correto do markdown inicial no mount. NÃO no modo colaborativo (o
  // conteúdo vem do Y.Doc; setContent duplicaria/conflitaria com o sync).
  useEffect(() => {
    if (isCollab) {
      return;
    }
    if (editor && value) {
      editor.commands.setContent(value);
      lastEmittedRef.current = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  // Sincroniza com mudanças externas de `value` (apenas modo não-colaborativo).
  useEffect(() => {
    if (isCollab || !editor) {
      return;
    }
    if (value !== lastEmittedRef.current) {
      editor.commands.setContent(value);
      lastEmittedRef.current = value;
    }
  }, [editor, isCollab, value]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  // Seed colaborativo: quando o provider sincroniza e o documento Yjs está
  // VAZIO (room novo), só o peer AUTORITATIVO (canSeed) popula com o markdown
  // atual. Convidados (canSeed=false) NUNCA semeiam — só recebem.
  //
  // GUARDA DE TIMING (contra a race de seed): `onSynced` pode disparar num
  // `yjs-init` prematuro/vazio (DO reciclado, update: null) ANTES do `yjs-sync`
  // que carrega o conteúdo real. Por isso adiamos o seed por um tick curto e
  // RE-CHECAMOS se o doc ainda está vazio no momento do disparo. Se um yjs-sync
  // com conteúdo real chegou nesse meio-tempo (Y.applyUpdate popula o doc), o
  // re-check vê isEmpty=false e o seed é cancelado — mesmo para o dono. Assim a
  // invariante é "semear só se realmente vazio depois do sync assentar".
  useEffect(() => {
    if (!collab || !editor) {
      return undefined;
    }
    let seedTimer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = collab.provider.onSynced(() => {
      // Re-checagem adiada: deixa qualquer yjs-sync com conteúdo real assentar
      // primeiro. ~200ms é imperceptível e cobre o gap init→sync do relay.
      seedTimer = setTimeout(() => {
        seedTimer = null;
        const isEmpty = editor.state.doc.textContent.trim().length === 0;
        if (shouldSeed({ isEmpty, canSeed, seedMarkdown })) {
          editor.commands.setContent(seedMarkdown as string);
        }
      }, 200);
    });
    return () => {
      if (seedTimer !== null) {
        clearTimeout(seedTimer);
        seedTimer = null;
      }
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collab, editor]);

  return (
    <div className="kindraw-rte kindraw-rte--headless">
      {editable && editor ? (
        <>
          <BubbleMenu
            editor={editor}
            className="kindraw-rte__bubble"
            options={{ placement: "top" }}
          >
            <RichTextAiMenu editor={editor} />
            <span className="kindraw-rte__menu-sep" />
            <MenuButton
              active={editor.isActive("bold")}
              label={t("kindraw.hybrid.format.bold")}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <strong>B</strong>
            </MenuButton>
            <MenuButton
              active={editor.isActive("italic")}
              label={t("kindraw.hybrid.format.italic")}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <em>I</em>
            </MenuButton>
            <MenuButton
              active={editor.isActive("underline")}
              label={t("kindraw.hybrid.format.underline")}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              <span style={{ textDecoration: "underline" }}>U</span>
            </MenuButton>
            <MenuButton
              active={editor.isActive("strike")}
              label={t("kindraw.hybrid.format.strikethrough")}
              onClick={() => editor.chain().focus().toggleStrike().run()}
            >
              <s>S</s>
            </MenuButton>
            <MenuButton
              active={editor.isActive("highlight")}
              label={t("kindraw.hybrid.format.highlight")}
              onClick={() => editor.chain().focus().toggleHighlight().run()}
            >
              <mark className="kindraw-rte__menu-mark">H</mark>
            </MenuButton>
            <MenuButton
              active={editor.isActive("code")}
              label={t("kindraw.hybrid.format.code")}
              onClick={() => editor.chain().focus().toggleCode().run()}
            >
              {"</>"}
            </MenuButton>
            <span className="kindraw-rte__menu-sep" />
            <MenuButton
              active={editor.isActive("link")}
              label={t("kindraw.hybrid.format.link")}
              onClick={() => {
                const previous = editor.getAttributes("link").href as
                  | string
                  | undefined;
                const url = window.prompt(
                  t("kindraw.hybrid.linkPrompt"),
                  previous || "https://",
                );
                if (url === null) {
                  return;
                }
                if (url === "") {
                  editor.chain().focus().unsetLink().run();
                  return;
                }
                editor
                  .chain()
                  .focus()
                  .extendMarkRange("link")
                  .setLink({ href: url })
                  .run();
              }}
            >
              <KindrawIcon name="link" size={14} />
            </MenuButton>
          </BubbleMenu>
        </>
      ) : null}
      <EditorContent className="kindraw-rte__surface" editor={editor} />
    </div>
  );
};
